<!--
 * Path: src/features/browser/forms/markdown/Markdown.vue
 * Task: Wrapper component utilizing FormBlockWrapper for markdown text areas.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="label || 'Description'"
    :info="info"
    :can-edit="canEdit"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="$emit('click:info')"
  >
    <template #read>
      <MarkdownRead
        :model-value="modelValue"
        :label="label"
        :info="info"
        :hide-header="true"
        @click:info="$emit('click:info')"
      />
    </template>
    <template #edit>
      <MarkdownEdit
        v-model="modelValue"
        :label="label"
      />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import MarkdownRead from './MarkdownRead.vue';
import MarkdownEdit from './MarkdownEdit.vue';

interface Props {
  label?: string;
  disabled?: boolean;
  info?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  disabled: false,
  info: ''
});

const emit = defineEmits<{
  (e: 'click:info'): void;
}>();

const modelValue = defineModel<string>({ default: '' });

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};
</script>
